import React, { useState, useRef, useCallback } from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import InputMask from "react-input-mask";
import { Field, Form, Formik } from "formik";
import { FormContainer } from "./styles";
import { trackClick, MARKETPLACE_TAG_IDS } from "../../utils/gtm";

export default function FormAddress({
  address,
  handleCancel,
  handleSubmit,
  highlightMissingFields,
}) {
  const [loadingCep, setLoadingCep] = useState(false);
  const debounceTimerRef = useRef(null);

  const trackAddressEvent = (title) => {
    const contentType =
      address && Object.keys(address).length > 0
        ? "alterar-endereco"
        : "cadastrar-novo-endereco";
    trackClick({
      title,
      screenName: "minha-conta",
      contentType,
      tagId: MARKETPLACE_TAG_IDS.clickAddressForm,
    });
  };

  const getAddressByCep = useCallback((cep, setFieldValue, setFieldError) => {
    setFieldValue("cep", cep);

    const clearValues = () => {
      setFieldValue("streetName", "");
      setFieldValue("city", "");
      setFieldValue("neighborhood", "");
      setFieldValue("state", "");
    };

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length >= 8) {
      setLoadingCep(true);
      debounceTimerRef.current = setTimeout(() => {
        fetch(`https://viacep.com.br/ws/${cleanCep}/json`)
          .then((response) => response.json())
          .then((data) => {
            if (data.erro) {
              clearValues();
              setFieldError("cep", "error");
              return false;
            }

            setFieldError("cep", "");
            setFieldError("streetName", "");
            setFieldError("city", "");
            setFieldError("neighborhood", "");
            setFieldError("state", "");

            setFieldValue("streetName", data.logradouro);
            setFieldValue("city", data.localidade);
            setFieldValue("neighborhood", data.bairro);
            setFieldValue("state", data.uf);

            setLoadingCep(false);
          })
          .catch(() => {
            clearValues();
            setFieldError("cep", "erro");

            setLoadingCep(false);
          });
      }, 500);
    } else {
      setLoadingCep(false);
      if (cleanCep.length > 0) {
        setFieldError("cep", "CEP inválido");
      }
    }
  }, []);

  const validationSchema = yup.object({
    cep: yup.string().required(),
    recipient: yup.string().notRequired(),
    label: yup.string().required(),
    streetName: yup.string().required(),
    number: yup.number().typeError("Somente números").required(),
    complement: yup.string().notRequired(),
    neighborhood: yup.string().required(),
    city: yup.string().required(),
    state: yup.string().required(),
    reference: yup.string().notRequired(),
    main: yup.string().notRequired(),
  });

  const initialValues = {
    recipient: address?.recipient ?? "",
    label: address?.label ?? "",
    cep: address?.zipCode ?? "",
    streetName: address?.streetName ?? "",
    number: address?.number ?? "",
    complement: address?.complement ?? "",
    neighborhood: address?.neighborhood ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    reference: address?.reference ?? "",
    main: address?.main ?? "0",
    fixed: address?.fixed,
  };

  const missingFieldsErrors = {};
  const missingFieldsTouched = {};
  if (highlightMissingFields) {
    const requiredFields = [
      "cep",
      "label",
      "streetName",
      "number",
      "neighborhood",
      "city",
      "state",
    ];
    requiredFields.forEach((key) => {
      const value = initialValues[key];
      if (value === "" || value === null || value === undefined) {
        missingFieldsErrors[key] = "Campo obrigatório";
        missingFieldsTouched[key] = true;
      }
    });
  }

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        initialErrors={missingFieldsErrors}
        initialTouched={missingFieldsTouched}
        validationSchema={validationSchema}
        enableReinitialize={false}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, actions) => {
          try {
            const formValues = {
              ...values,
              content_type:
                Object.keys(address).length > 0
                  ? "alterar-endereco"
                  : "cadastrar-novo-endereco",
            };

            trackAddressEvent("adicionar");

            const response = await handleSubmit(formValues);

            !address.uuid && actions.resetForm();
          } catch (error) {
            console.error("Erro ao salvar:", error);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          touched,
          setFieldValue,
          setFieldError,
          validateField,
          isSubmitting,
          values,
        }) => {
          return (
            <>
              <div className="mb-4 form-address">
                <h2 className="form-address__title font-wl-600 text-center">
                  {!Object.keys(address).length
                    ? "Cadastrar novo endereço"
                    : "Atualizar dados do endereço"}
                </h2>
              </div>
              <Form className="form-address">
                <div className="mb-2">
                  <div className="input-group">
                    <Field
                      name="label"
                      className={`input-custom form-address__input ${
                        errors.label && touched.label ? "is-invalid" : null
                      }`}
                      placeholder="Identificar este endereço como"
                      maxLength={30}
                    />
                  </div>
                  <div className="mb-2">
                    <div className="input-group">
                      <Field name="cep">
                        {({ field }) => (
                          <div style={{ width: "100%" }}>
                            <InputMask
                              className={`input-custom form-address__input ${
                                errors.cep && touched.cep ? "is-invalid" : null
                              }`}
                              mask="99999-999"
                              maskChar={false}
                              type="tel"
                              inputMode="numeric"
                              {...field}
                              placeholder="CEP"
                              onChange={(e) =>
                                getAddressByCep(
                                  e.target.value,
                                  setFieldValue,
                                  setFieldError,
                                )
                              }
                            />
                            {errors.cep && touched.cep && (
                              <div className="invalid-feedback d-block italic">
                                cep inválido
                              </div>
                            )}
                          </div>
                        )}
                      </Field>

                      <span className="input-group-wl mt-1">
                        {loadingCep && (
                          <span
                            className="spinner-border spinner-border-xs"
                            role="status"
                          />
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <Field
                      className={`input-custom form-address__input ${
                        errors.streetName && touched.streetName
                          ? "is-invalid"
                          : null
                      }`}
                      name="streetName"
                      placeholder="Endereço"
                    />
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.number && touched.number ? "is-invalid" : null
                        }`}
                        name="number"
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Número"
                        onBlur={() => validateField("number")}
                      />
                    </div>

                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.complement && touched.complement
                            ? "is-invalid"
                            : null
                        }`}
                        name="complement"
                        placeholder="Complemento"
                        onBlur={() => validateField("complement")}
                      />
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.neighborhood && touched.neighborhood
                            ? "is-invalid"
                            : null
                        }`}
                        name="neighborhood"
                        placeholder="Bairro"
                      />
                    </div>
                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.city && touched.city ? "is-invalid" : null
                        }`}
                        name="city"
                        placeholder="Cidade"
                      />
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.state && touched.state ? "is-invalid" : null
                        }`}
                        name="state"
                        placeholder="Estado"
                      />
                    </div>
                    <div className="col-6">
                      <Field
                        className={`input-custom form-address__input ${
                          errors.reference && touched.reference
                            ? "is-invalid"
                            : null
                        }`}
                        name="reference"
                        placeholder="Referência"
                        onBlur={() => validateField("reference")}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center  justify-content-center my-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-default-wl"
                    style={{
                      width: 187,
                      height: 36.45,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-border-sm "
                        role="status"
                      />
                    ) : (
                      "ADICIONAR"
                    )}
                  </button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </FormContainer>
  );
}

FormAddress.propTypes = {
  address: PropTypes.any,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func,
  highlightMissingFields: PropTypes.bool,
};
